import React from "react";
import api from "../../Auth/Api";
import { Button, Box, TextField, Typography } from "@mui/material";
import { PageContainer, SectionHeader, SelectField, FormDialog, formatDate, preWrap } from "../../Layout/Common";
import PagedTable from "../../Layout/PagedTable";
import { subscribePoll } from "../../Layout/Poller";
import { AttachableTextField, AttachmentList, stripAttachmentLinks, uploadAll, disposePending } from "../../Layout/Attachments";

const KINDS = ["task", "note", "question", "result"];
const FILTER_KINDS = ["task", "note", "question", "result", "report", "decision-request"];
const PAGE = 20;
const NEW_MESSAGE = { to: "broadcast", kind: "task", body: "", pending: [] };

// Messages: server-paged (20/page) with server-side filters; composing happens in a dialog (paste images there).
export default class MessagesPage extends React.Component {
    state = { messages: [], total: 0, page: 1, team: [], q: "", from: "", to: "", kind: "", compose: null, attachError: "", sending: false, progress: "" };
    componentDidMount() { this.load(); this.loadTeam(); this.unsub = subscribePoll(this.load, 10000); }
    componentWillUnmount() { if (this.unsub) this.unsub(); if (this.debounce) clearTimeout(this.debounce); disposePending(this.state.compose?.pending); }
    // Coalescing guard: a load requested while one runs is re-run once it ends (never dropped, never overlapped).
    load = async () => {
        if (this.loading) { this.pending = true; return true; }
        this.loading = true;
        try {
            const { page, q, from, to, kind } = this.state;
            const r = await api.development("Loading messages", ["messages"], { silent: true, payload: { skip: (page - 1) * PAGE, limit: PAGE, q, from, to, kind } });
            if (r && r.messages) this.setState({ messages: r.messages, total: r.total || 0 });
            return true;
        } catch { return false; }
        finally {
            this.loading = false;
            if (this.pending) { this.pending = false; this.load(); }
        }
    };
    setPage = (p) => this.setState({ page: p }, this.load);
    setFilter = (patch, debounce) => {
        this.setState({ ...patch, page: 1 });
        if (this.debounce) clearTimeout(this.debounce);
        if (debounce) this.debounce = setTimeout(this.load, 400); else setTimeout(this.load, 0);
    };
    loadTeam = () => api.development("Loading team", ["team"], { silent: true })
        .then(r => { if (r && r.team) this.setState({ team: r.team }); })
        .catch(() => {});
    send = async () => {
        const { compose, sending } = this.state;
        if (!compose || !compose.body.trim() || sending) return;   // a body is required (the backend rejects attachment-only messages)
        this.setState({ sending: true, attachError: "", progress: "" });
        try {
            const attachments = compose.pending.length ? await uploadAll(compose.pending, p => this.setState({ progress: p })) : [];
            await api.development("Sending message", ["post-message"], { payload: { to: compose.to, kind: compose.kind, body: compose.body, attachments } });
            this.closeCompose(true); this.load();
        } catch (e) { this.setState({ attachError: e.message || String(e) }); }
        finally { this.setState({ sending: false, progress: "" }); }
    };
    // Closing (or sending) drops the pending files and their preview URLs.
    closeCompose = (force) => { if (!force && this.state.sending) return; disposePending(this.state.compose?.pending); this.setState({ compose: null, attachError: "" }); };
    render() {
        const { messages, total, page, team, q, from, to, kind, compose, attachError, sending, progress } = this.state;
        const names = team.map(a => a.name);
        const any = { value: "", label: "Any" };
        const patch = (p) => this.setState({ compose: { ...compose, ...p } });
        return (
            <PageContainer maxWidth={1100}>
                <SectionHeader title="Messages">
                    <Button variant="contained" onClick={() => this.setState({ compose: { ...NEW_MESSAGE }, attachError: "" })}>New message</Button>
                </SectionHeader>
                <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <TextField size="small" label="Search text" value={q} sx={{ minWidth: 240 }} onChange={e => this.setFilter({ q: e.target.value }, true)} />
                    <SelectField label="From" size="small" minWidth={140} value={from} onChange={v => this.setFilter({ from: v })} options={[any, ...names, "human", "simulator", "system"]} />
                    <SelectField label="To" size="small" minWidth={140} value={to} onChange={v => this.setFilter({ to: v })} options={[any, "broadcast", "human", ...names]} />
                    <SelectField label="Kind" size="small" minWidth={150} value={kind} onChange={v => this.setFilter({ kind: v })} options={[any, ...FILTER_KINDS]} />
                    <Typography variant="caption" sx={{ color: "text.secondary", ml: 1 }}>{total} message{total === 1 ? "" : "s"}</Typography>
                </Box>
                <PagedTable
                    rows={messages} total={total} page={page} pageSize={PAGE} onPage={this.setPage}
                    columns={[
                        { id: "createdDate", label: "Date", sx: { whiteSpace: "nowrap" }, render: m => formatDate(m.createdDate) },
                        { id: "from", label: "From" },
                        { id: "to", label: "To" },
                        { id: "kind", label: "Kind" },
                        { id: "body", label: "Body", width: "55%", sx: preWrap, render: m => <>{stripAttachmentLinks(m.body, m.attachments)}<AttachmentList attachments={m.attachments} /></> },
                        { id: "status", label: "Status" },
                    ]}
                    getRowKey={m => m.id}
                />
                <FormDialog open={!!compose} title="New message" onClose={() => this.closeCompose()} onConfirm={this.send}
                    confirmText={sending ? (progress || "Sending…") : "Send"} confirmDisabled={!compose || !compose.body.trim() || sending} cancelDisabled={sending} maxWidth="sm" fullWidth>
                    {compose && <>
                        <Box sx={{ display: "flex", gap: 1, my: 1 }}>
                            <SelectField label="To" size="small" fullWidth value={compose.to} onChange={v => patch({ to: v })} options={["broadcast", ...names]} />
                            <SelectField label="Kind" size="small" minWidth={130} value={compose.kind} onChange={v => patch({ kind: v })} options={KINDS} />
                        </Box>
                        <AttachableTextField label="Message (paste images here)" minRows={6} autoFocus value={compose.body} onChange={v => patch({ body: v })}
                            pending={compose.pending} onPending={p => patch({ pending: p })} error={attachError} onError={err => this.setState({ attachError: err })} />
                    </>}
                </FormDialog>
            </PageContainer>
        );
    }
}
